import React, { FC, useState, useCallback, memo } from 'react';
import { Button } from 'antd';
import { AnnotationStandardDetails } from '../../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import { getAllStandardProperties, initialColumns, insertFindingTypeColumns } from '../Helpers';
import styles from '../MaintenanceTriggerForm.module.scss';
import Table from './Table';
import Busy from '../../../Shared/Busy';
import { IdText, ResponseLevel } from '../MaintenanceTriggerForm.slice';
import { FormDataInitial } from '../formSelector';
import ImportComponent from '../ImportCSV';
import { mapMaintenaceTriggerToRule, mapMaintenanceTriggers, validateMaintenanceTriggerRawRow } from '../ImportHelpers';
import CSVFile from '../../../../assets/templates/mtTemplate.csv';

type Props = {
  standard: AnnotationStandardDetails | null;
  rules: any[];
  isLoading: boolean;
  responseLevels: ResponseLevel[];
  formData: FormDataInitial;
  setRules: (rules: any[]) => void;
};

const Rules: FC<Props> = memo(({ isLoading, standard, rules, responseLevels, formData, setRules }) => {
  const [scrollToStart, setScrollToStart] = useState(false);
  const [columns, setColumns] = useState<IdText[]>(initialColumns);

  const onFindingTypeSelected = useCallback(
    (newColumns: IdText[]) => {
      if (!standard) return;

      const uniqueFTColumns = getAllStandardProperties(standard);

      const columnsToAdd: IdText[] = uniqueFTColumns.filter(newColumn => !columns.find(x => x.id === newColumn.id));

      setColumns(insertFindingTypeColumns(columns, columnsToAdd));
    },
    [columns, standard]
  );

  const onAddNewRow = useCallback(() => {
    const newRule: any = {
      index: rules.length + 1
    };

    initialColumns.forEach(column => {
      newRule[column.id] = '';
    });

    setRules([...rules, newRule]);
    setScrollToStart(true);
  }, [rules, setRules]);

  if (isLoading) {
    return (
      <div>
        <Busy />
      </div>
    );
  }

  if (!standard) {
    return null;
  }

  //TODO: IMPROVE ASAP
  const notAllowedRL = ['Under Investigation', 'N/A'];
  const filteredResponseLevels = responseLevels.filter(x => !notAllowedRL.includes(x.code));

  return (
    <div className='ant-table-wrapper'>
      <ImportComponent
        onFileParsed={(rawData: any[]) => {
          const data = mapMaintenanceTriggers(rawData);

          // Handle the imported data
          validateMaintenanceTriggerRawRow(data, standard, responseLevels);

          const uniqueFTColumns = getAllStandardProperties(standard);

          data.forEach(element => {
            const findingType = standard.findingTypes.find(x => x.findingTypeTemplate?.name === element.FindingType);
            element.FindingType = findingType?.id!;

            const responseLevel = responseLevels.find(x => x.code === element.ResponseLevel);
            element.ResponseLevel = responseLevel?.id!;
            element.ResponseLevelDescription = responseLevel?.description!;
          });

          onFindingTypeSelected(uniqueFTColumns);

          const mappedData = mapMaintenaceTriggerToRule(data, rules.length);

          setRules([...rules, ...mappedData]);
        }}
        onDownloadTemplateClicked={() => {
          const link = document.createElement('a');
          link.href = CSVFile;
          link.download = 'MaintenanceTriggerRulesTemplate.csv';
          link.click();
        }}
        onClearAllClicked={() => setRules([])}
      />
      <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Response levels rules</span>
      <Table
        standard={standard}
        rules={rules}
        setRules={setRules}
        scrollToStart={scrollToStart}
        onScrollToStart={() => setScrollToStart(false)}
        responseLevels={filteredResponseLevels}
        formData={formData}
        columns={columns}
        onFindingTypeSelected={onFindingTypeSelected}
      />
      <div className={styles.addRule}>
        <Button type='default' onClick={onAddNewRow}>
          Add new row
        </Button>
      </div>
    </div>
  );
});

export default Rules;
